import { creatorbase, creatorbaseQueries } from '@round/api';
import { GenericDropdownOption } from 'App.types';
import useSelectProps from 'Hooks/useSelectProps';
import { useInfiniteQuery } from '@tanstack/react-query';
import { SelectProps } from '@round/ui-kit';
import { isNumber } from 'utility/utility';
import { useMemo } from 'react';
import uniq from 'lodash/uniq';
import { useBrandsQueriesByIds, useProject, useTeamsQueriesByIds } from '@round/creatorbase-helpers/api-hooks';

const queryOptions = creatorbaseQueries.project.options.infinite;

export type ProjectOption = GenericDropdownOption<number> &
    creatorbase.Project & {
        brand?: creatorbase.Brand | null;
        team?: creatorbase.Team | null;
    };

export const mapProjectToOption = (
    project: creatorbase.Project,
    brand?: creatorbase.Brand | null | undefined,
    team?: creatorbase.Team | null | undefined
): ProjectOption => ({
    value: project.id,
    label: project.name,
    ...project,
    brand,
    team,
});

type Params = {
    initOn: 'menuOpen' | 'mount';
    initialValueId?: number;
    params?: Omit<creatorbase.GetProjectsParams, 'page_size' | 'search'>;
    enabled?: boolean;
};

type Return = {
    getProps: (override?: Pick<SelectProps<ProjectOption>, 'onChange'>) => SelectProps<ProjectOption>;
};

export default function useProjectSelect({ enabled = true, ...params }: Params = { initOn: 'mount' }): Return {
    const { data: initialProject } = useProject(params.initialValueId, { enabled: isNumber(params.initialValueId) });
    const initialValue = useMemo(() => (initialProject ? mapProjectToOption(initialProject) : null), [initialProject]);

    const { getProps, isMenuOpen, debouncedSearchValue } = useSelectProps<ProjectOption, false>({ initialValue });

    const { data, isFetching, hasNextPage, fetchNextPage, isError } = useInfiniteQuery({
        ...queryOptions({ ...params.params, page_size: 25, search: debouncedSearchValue }),
        enabled: enabled && ((params.initOn === 'menuOpen' && isMenuOpen) || params.initOn === 'mount'),
    });

    const brandIds = data?.pages.map((p) => uniq(p.results.map((project) => project.brand_id))) ?? [];
    const brandQueries = useBrandsQueriesByIds(brandIds);

    const teamIds =
        data?.pages.map((_, pageIndex) => uniq(brandQueries[pageIndex]?.data?.results.map((b) => b.team_id) ?? [])) ??
        [];

    const teamsQueries = useTeamsQueriesByIds(teamIds);

    const options: ProjectOption[] =
        data?.pages.flatMap((page, pageIndex) => {
            const brandQuery = brandQueries[pageIndex];
            const teamsQuery = teamsQueries[pageIndex];

            if (!brandQuery?.isSuccess || !teamsQuery?.isSuccess) {
                return [];
            }

            return page.results.map((p) => {
                const brand = brandQuery?.data?.results.find((b) => b.id === p.brand_id);
                const team = teamsQuery?.data?.results.find((t) => t.id === brand?.team_id);
                return mapProjectToOption(p, brand, team);
            });
        }) ?? [];

    return {
        getProps: (override) =>
            getProps({
                ...override,
                options,
                noOptionsMessage: () => (isError ? 'Could not get projects' : 'No projects found'),
                isLoading:
                    isFetching || brandQueries.some((r) => r.isFetching) || teamsQueries.some((r) => r.isFetching),
                onMenuScrollToBottom: () => {
                    if (hasNextPage && !isFetching) {
                        fetchNextPage();
                    }
                },
                filterOption: null,
            }),
    };
}
